/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../common/controller/list-helper.service';
import {ChangeDetectorRef, Component, DoCheck, Inject, Input, NgZone, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {NgForm} from '@angular/forms';
import * as _ from 'lodash';
import template from './earnings-account-analysis-page.component.html';
import {safeDetectChanges} from '../common/util/safe-detect-changes';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';
import {SiteBulkActionService} from '../site/site-bulk-action.service';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {SiteAccessModalComponent} from './site-access-modal.component';
import {SiteResource} from '../../components/api/resources/resource/site.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {StateService} from '@uirouter/core';

type AnalysisTab = 'sites' | 'charts' | 'reports';

@Component({
  standalone: false,
  selector: 'adn-earnings-account-analysis-page',
  template
})
export class EarningsAccountAnalysisPageComponent implements DoCheck, OnChanges, OnDestroy {
  @Input() model: any;
  @Input() modelComms: any;
  @Input() isNew: boolean;

  effectiveIsNew = false;
  apiModel: any;
  activeTab: AnalysisTab = 'sites';

  sites: any = {};
  /** Snapshot — list-helper replaces sites.items on settle (NG0100). */
  displayItems: any[] = [];
  hasItems = false;
  site: any;
  showSiteForm = false;
  copySite: any = {site: null};
  showAdvAccess = false;
  teamReport: any;
  showNotes = false;
  adminUserName = '';
  canSeeReports = false;
  visibleReports = false;

  bulkAction: () => void;

  private lastLoadedModelId: string | undefined;
  private subscribedModelComms: any;
  private destroyed = false;
  private sitesInitialized = false;

  constructor(
    private readonly $state: StateService,
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LocalUserProfile) localUserProfile: any,
    private readonly adnListHelper: AdnListHelperService,
    @Inject(SiteResource) private readonly Site: any,
    private readonly siteBulkActionService: SiteBulkActionService,
    private readonly angularModalService: AngularModalService,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
    const username = localUserProfile.get('username');
    if (_.includes(['adnuntius@adnuntius.com', 'broker1@adnuntius.com'], username)) {
      this.showNotes = true;
      this.adminUserName = username;
    }
    this.canSeeReports = LocalUserPermissions.getAnyVisibility().RUN_REPORTS === true;
    this.bulkAction = () => undefined;
  }

  get showNewMessage(): boolean {
    return !!this.effectiveIsNew;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      this.effectiveIsNew = !!this.isNew;
    }
    if (changes.model) {
      this.apiModel = this.model;
      this.updateAdvAccess();
    }
    if (changes.modelComms) {
      this.subscribeModelComms();
    }
    this.refreshSitesIfNeeded();
  }

  ngDoCheck(): void {
    const modelId = this.model?.id;
    if (modelId && modelId !== this.lastLoadedModelId && !this.effectiveIsNew) {
      this.refreshSitesIfNeeded();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  selectTab(tab: AnalysisTab): void {
    this.activeTab = tab;
    if (tab === 'reports') {
      this.visibleReports = true;
    }
    if (tab === 'sites' && this.showAdvAccess && !this.teamReport) {
      this.loadSiteGroupsQuery();
    }
  }

  loadSiteForm(): void {
    const theCountry = _.isArray(this.model?.countries) ? this.model.countries[0] : null;
    this.site = this.Site.create({
      earningsAccount: this.model
    });
    if (theCountry) {
      this.site.country = theCountry;
    }
    const ownerTeamId = _.get(this.model, ['team', 'id']);
    if (ownerTeamId) {
      this.site.ownerTeam = {id: ownerTeamId};
    }
    this.showSiteForm = true;
  }

  loadExternalSite(): void {
    const theCountry = _.isArray(this.model?.countries) ? this.model.countries[0] : null;
    this.$state.go('app.inv.sites.new', {
      earningsAccountName: this.model?.name,
      earningsAccountId: _.get(this.model, ['id']),
      earningsAccountCountry: theCountry,
      earningsAccountOwnerTeamId: _.get(this.model, ['team', 'id'])
    });
  }

  onSiteBeforeSubmit(): Promise<any> | void {
    if (!_.get(this.copySite, 'site') || _.isArray(this.copySite.site?.teams)) {
      return;
    }
    return this.Site.get({id: this.copySite.site.id}).$promise.then((theSite: any) => {
      this.site.teams = theSite.teams;
    });
  }

  saveSite(editForm: NgForm): void {
    if (editForm.invalid || this.sites.query?.$pending) {
      return;
    }
    const beforeSubmit = this.onSiteBeforeSubmit();
    const savePromise = beforeSubmit ? beforeSubmit.then(() => this.site.$save()) : this.site.$save();
    savePromise.then((saved: any) => {
      this.ngZone.run(() => {
        this.showSiteForm = false;
        if (!_.isArray(this.sites.items)) {
          this.sites.items = [];
        }
        this.sites.addItemToList?.(saved);
        safeDetectChanges(this.cdr);
      });
    });
  }

  editAccess(teamReport: any): void {
    const modalRef = this.angularModalService.show(SiteAccessModalComponent, {
      class: 'modal-lg',
      initialState: {teamReport}
    });
    (modalRef.content as SiteAccessModalComponent).onSaved.subscribe(() => {
      this.loadSiteGroupsQuery();
    });
  }

  expandSiteLimit(team: any): void {
    team.siteLimit = team.sites.length;
  }

  expandTeamLimit(team: any): void {
    team.teamLimit = team.teams.length;
  }

  expandAdditionalTeamLimit(team: any): void {
    team.additionalTeamLimit = team.additionalTeams.length;
  }

  expandRemovedTeamLimit(team: any): void {
    team.removedTeamLimit = team.removedTeams.length;
  }

  private subscribeModelComms(): void {
    if (!this.modelComms || this.subscribedModelComms === this.modelComms) {
      return;
    }
    this.subscribedModelComms = this.modelComms;
    this.modelComms.addSubscriber((data: any, type: any) => {
      if (this.destroyed || type) {
        return;
      }
      this.ngZone.run(() => {
        if (data?.name) {
          this.model = data;
          this.effectiveIsNew = false;
          this.updateAdvAccess();
          this.refreshSitesIfNeeded(true);
        }
        safeDetectChanges(this.cdr);
      });
    });
  }

  private updateAdvAccess(): void {
    this.showAdvAccess = this.LocalNetworkProfile.isPureMarketplacePlatform()
      && !this.effectiveIsNew
      && this.model?.filterTeamsType === 'NONE';
    if (this.showAdvAccess && this.activeTab === 'sites') {
      this.loadSiteGroupsQuery();
    }
  }

  private loadSiteGroupsQuery(): void {
    if (!this.model?.id) {
      return;
    }
    this.teamReport = false;
    this.Site.query({groupByTeams: 'true', earningsAccount: this.model.id}, (page: any) => {
      // false → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.teamReport = page.results;
        _.forEach(this.teamReport, (team: any) => {
          team.teamLimit = 10;
          team.siteLimit = 10;
          team.additionalTeamLimit = 10;
          team.removedTeamLimit = 10;
        });
      });
    });
  }

  private refreshSitesIfNeeded(force = false): void {
    const modelId = this.effectiveIsNew ? undefined : this.model?.id;
    if (!force && modelId === this.lastLoadedModelId) {
      return;
    }
    this.lastLoadedModelId = modelId;
    this.initSitesList();
  }

  private initSitesList(): void {
    if (!this.model?.id || this.effectiveIsNew) {
      this.sites = {};
      this.sitesInitialized = false;
      this.displayItems = [];
      this.hasItems = false;
      return;
    }
    if (this.sitesInitialized && this.sites?.moreParams?.earningsAccount === this.model.id) {
      return;
    }
    this.sitesInitialized = true;
    this.sites = {};
    this.displayItems = [];
    this.hasItems = false;
    this.adnListHelper.setUpBasicList(this.sites, this.Site, 'fullSitesForEarningsAccount', {
      moreParams: {earningsAccount: this.model.id},
      afterFill: () => {
        deferMarkForCheck(this.cdr, () => {
          this.displayItems = (this.sites.items || []).slice();
          this.hasItems = this.displayItems.length > 0;
          this.copySite.site = this.displayItems[0];
          this.bulkAction = this.siteBulkActionService.createBulkAction(this.sites);
        });
      }
    });
    this.sites.makeQuery?.();
  }
}
